import { Icon } from '../icons';
import React, { useMemo } from 'react';

import AssetUploader from './AssetUploader';
import { ProjectAsset } from '../../types/editor';

interface AddAudioModalProps {
  projectId: number;
  /** Full asset list — we filter to audio inside so the parent doesn't
   *  have to. */
  assets: ProjectAsset[];
  /** Timeline time the user clicked. Shown in the header so they know
   *  where the clip will land. */
  atTime: number;
  /** Pick an existing audio asset → wires straight to ADD_CLIP. */
  onPick: (asset: ProjectAsset) => void;
  /** Newly uploaded asset → caller adds to state + adds a clip. */
  onUploaded: (asset: ProjectAsset) => void;
  onClose: () => void;
}

function fmtSec(t: number | null | undefined): string {
  if (!t || !isFinite(t)) return '—';
  const m = Math.floor(t / 60);
  const s = Math.floor(t - m * 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function fmtTimeMSC(t: number): string {
  if (!isFinite(t) || t < 0) return '0:00:00';
  const totalCs = Math.round(t * 100);
  const m = Math.floor(totalCs / 6000);
  const s = Math.floor((totalCs % 6000) / 100);
  const cs = totalCs % 100;
  return `${m}:${String(s).padStart(2, '0')}:${String(cs).padStart(2, '0')}`;
}

export default function AddAudioModal({
  projectId, assets, atTime, onPick, onUploaded, onClose,
}: AddAudioModalProps) {

  const audioAssets = useMemo(
    () => assets.filter((a) => a.kind === 'audio'),
    [assets],
  );

  return (
    <div
      className="modal modal--centered"
      role="dialog"
      aria-modal="true"
      aria-label="Add audio"
    >
      <div className="modal__panel">
        <header className="modal__head">
          <h3>Add audio <span className="muted">@ {fmtTimeMSC(atTime)}</span></h3>
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            aria-label="Close"
          ><Icon name="close" /></button>
        </header>

        <div className="modal__body">
          <section className="add-audio-section">
            <div className="section-label">From project assets</div>
            {audioAssets.length === 0 ? (
              <p className="muted">No audio assets in this project yet — upload one below.</p>
            ) : (
              <ul className="add-audio-list">
                {audioAssets.map((a) => (
                  <li key={a.id}>
                    <button
                      type="button"
                      className="add-audio-tile"
                      onClick={() => onPick(a)}
                    >
                      <span className="add-audio-icon" aria-hidden>♪</span>
                      <span className="add-audio-name" title={a.original_filename}>
                        {a.original_filename || `Audio #${a.id}`}
                      </span>
                      <span className="muted add-audio-dur">{fmtSec(a.duration)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="add-audio-section">
            <div className="section-label">Or upload a new file</div>
            <AssetUploader
              projectId={projectId}
              onUploaded={(asset) => {
                /* AssetUploader accepts image / video / audio. Only
                 * route audio uploads back through onUploaded — the
                 * other kinds would be confusing here (we'd drop a
                 * video onto the AUDIO track). The wider asset library
                 * is reachable via the Project Assets panel. */
                if (asset.kind === 'audio') onUploaded(asset);
              }}
            />
            <small className="muted">Audio files only — other kinds upload to the project but won't drop onto the audio track.</small>
          </section>
        </div>

        <footer className="modal__foot">
          <button type="button" className="ghost" onClick={onClose}>Cancel</button>
        </footer>
      </div>
    </div>
  );
}
